import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { FullCrudPage } from "@/components/admin/FullCrudPage";
import { CheckboxField, NumberField, SelectField, TextField } from "@/components/admin/fields";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { createUser, deleteUser, getUser, listUsers, updateUser } from "@/services/api/users";
import { listGroups } from "@/services/api/groups";
import type { User } from "@/services/api/types";

interface UserForm {
  email: string;
  full_name: string;
  password: string;
  is_active: boolean;
  session_profile: string;
  idle_timeout_minutes: string;
  preferred_locale: string;
  timezone: string;
  group_ids: number[];
}

const emptyForm: UserForm = {
  email: "",
  full_name: "",
  password: "",
  is_active: true,
  session_profile: "standard",
  idle_timeout_minutes: "",
  preferred_locale: "en",
  timezone: "Europe/London",
  group_ids: [],
};

export function UsersPage() {
  const { t } = useTranslation();
  const [offset, setOffset] = useState(0);
  const limit = 25;
  const query = useQuery({
    queryKey: ["users", offset],
    queryFn: () => listUsers({ limit, offset }),
  });
  const groups = useQuery({ queryKey: ["groups", "all"], queryFn: () => listGroups({ limit: 500 }) });

  const columns: ColumnDef<User>[] = [
    { accessorKey: "email", header: t("auth.email") },
    { accessorKey: "full_name", header: t("admin.fullName") },
    {
      accessorKey: "is_active",
      header: t("admin.active"),
      cell: ({ getValue }) => (getValue() ? t("common.yes") : t("common.no")),
    },
  ];

  const groupOptions = (groups.data?.items ?? []).map((g) => ({
    value: g.id.toString(),
    label: g.name,
  }));

  return (
    <FullCrudPage<User, UserForm>
      title={t("nav.users")}
      entityType="user"
      getRecord={getUser}
      columns={columns}
      data={query.data?.items ?? []}
      total={query.data?.total ?? 0}
      isLoading={query.isLoading}
      limit={limit}
      offset={offset}
      onPageChange={setOffset}
      queryKey="users"
      emptyForm={emptyForm}
      toForm={(r) => ({
        email: r.email,
        full_name: r.full_name,
        password: "",
        is_active: r.is_active,
        session_profile: r.session_profile,
        idle_timeout_minutes: r.idle_timeout_minutes?.toString() ?? "",
        preferred_locale: r.preferred_locale,
        timezone: r.timezone,
        group_ids: r.group_ids ?? [],
      })}
      canSubmit={(f) => Boolean(f.email && f.full_name)}
      renderForm={(form, setForm, mode) => (
        <div className="grid grid-cols-2 gap-4">
          <TextField
            label={t("auth.email")}
            value={form.email}
            onChange={(v) => setForm({ ...form, email: v })}
            required
          />
          <TextField
            label={t("admin.fullName")}
            value={form.full_name}
            onChange={(v) => setForm({ ...form, full_name: v })}
            required
          />
          <TextField
            label={mode === "create" ? t("admin.password") : t("admin.resetPassword")}
            value={form.password}
            onChange={(v) => setForm({ ...form, password: v })}
          />
          <SelectField
            label={t("admin.sessionProfile")}
            value={form.session_profile}
            onChange={(v) => setForm({ ...form, session_profile: v })}
            options={[
              { value: "standard", label: "Standard" },
              { value: "kiosk", label: "Kiosk" },
            ]}
          />
          <NumberField
            label={t("admin.idleTimeout")}
            value={form.idle_timeout_minutes}
            onChange={(v) => setForm({ ...form, idle_timeout_minutes: v })}
          />
          <SelectField
            label={t("admin.timezone")}
            value={form.timezone}
            onChange={(v) => setForm({ ...form, timezone: v })}
            options={[
              { value: "Europe/London", label: "Europe/London" },
              { value: "UTC", label: "UTC" },
            ]}
          />
          <div className="col-span-2 space-y-1">
            <Label>{t("admin.userGroups")}</Label>
            <div className="grid grid-cols-2 gap-1">
              {groupOptions.map((option) => {
                const id = Number(option.value);
                const checked = form.group_ids.includes(id);
                return (
                  <label key={id} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(v) =>
                        setForm({
                          ...form,
                          group_ids: v
                            ? [...form.group_ids, id]
                            : form.group_ids.filter((g) => g !== id),
                        })
                      }
                    />
                    {option.label}
                  </label>
                );
              })}
            </div>
          </div>
          <CheckboxField
            label={t("admin.active")}
            checked={form.is_active}
            onChange={(v) => setForm({ ...form, is_active: v })}
          />
        </div>
      )}
      onSave={async (mode, form, record) => {
        if (mode === "edit" && record) {
          const update: {
            email: string;
            full_name: string;
            password?: string;
            is_active: boolean;
            session_profile: string;
            idle_timeout_minutes: number | null;
            preferred_locale: string;
            timezone: string;
            group_ids: number[];
          } = {
            email: form.email,
            full_name: form.full_name,
            is_active: form.is_active,
            session_profile: form.session_profile,
            idle_timeout_minutes: form.idle_timeout_minutes
              ? Number(form.idle_timeout_minutes)
              : null,
            preferred_locale: form.preferred_locale,
            timezone: form.timezone,
            group_ids: form.group_ids,
          };
          if (form.password) update.password = form.password;
          return updateUser(record.id, update);
        }
        return createUser({
          email: form.email,
          full_name: form.full_name,
          password: form.password || "ChangeMe!123",
          is_active: form.is_active,
          session_profile: form.session_profile,
          idle_timeout_minutes: form.idle_timeout_minutes
            ? Number(form.idle_timeout_minutes)
            : null,
          preferred_locale: form.preferred_locale,
          timezone: form.timezone,
          group_ids: form.group_ids,
        });
      }}
      onDelete={(record) => deleteUser(record.id)}
      recordTitle={(r) => r.email}
      detailFields={(r) => [
        { label: t("auth.email"), value: r.email, field: "email" },
        { label: t("admin.fullName"), value: r.full_name, field: "full_name" },
        {
          label: t("admin.active"),
          value: r.is_active ? t("common.yes") : t("common.no"),
          field: "is_active",
        },
        { label: t("admin.sessionProfile"), value: r.session_profile, field: "session_profile" },
        { label: t("admin.idleTimeout"), value: r.idle_timeout_minutes, field: "idle_timeout_minutes" },
        {
          label: t("admin.userGroups"),
          value: (groups.data?.items ?? [])
            .filter((g) => (r.group_ids ?? []).includes(g.id))
            .map((g) => g.name)
            .join(", "),
        },
      ]}
    />
  );
}
